import React, { useState, useEffect } from 'react';
import { XMarkIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../../utils/formatters';
import walletService from '../services/walletService';
import { toast } from 'react-hot-toast';
import Portal from '../../../components/shared/Portal';

/**
 * Account Sharing Management Modal
 * Shows all accounts with toggle switches to manage sharing with partner
 */
const AccountSharingModal = ({ isOpen, onClose, accounts = {}, partnerName, onUpdate }) => {
  const [localAccounts, setLocalAccounts] = useState([]);
  const [updatingAccounts, setUpdatingAccounts] = useState(new Set());

  // Flatten all accounts into a single list when modal opens
  useEffect(() => {
    if (isOpen) {
      const allAccounts = [
        ...(accounts.personal || []).map(acc => ({
          ...acc,
          _ownedByUser: true,
          _ownerName: 'You'
        })),
        ...(accounts.partner || []).map(acc => ({
          ...acc,
          _ownedByUser: false,
          _ownerName: partnerName || 'Partner'
        }))
      ];
      
      // Sort: user's accounts first, then partner's
      allAccounts.sort((a, b) => {
        if (a._ownedByUser && !b._ownedByUser) return -1;
        if (!a._ownedByUser && b._ownedByUser) return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
      
      setLocalAccounts(allAccounts);
    }
  }, [isOpen, accounts, partnerName]);

  // Handle toggle for a specific account
  const handleToggle = async (account) => {
    if (!account._ownedByUser) {
      // Can't toggle partner's accounts
      toast.error("You can only manage sharing for your own accounts");
      return;
    }

    const accountId = account._id;
    setUpdatingAccounts(prev => new Set(prev).add(accountId));

    try {
      const currentSharedStatus = account.sharedWithPartner || account.visibilitySettings?.showToPartner || false;
      const newSharingStatus = !currentSharedStatus;

      const settings = {
        showToPartner: newSharingStatus,
        showTransactions: newSharingStatus,
        showBalance: newSharingStatus,
        showInNetWorth: newSharingStatus
      };

      const result = await walletService.updateSharingSettings(accountId, settings);

      if (result.success) {
        // Update local state
        setLocalAccounts(prev => prev.map(acc => {
          if (acc._id === accountId) {
            return {
              ...acc,
              sharedWithPartner: newSharingStatus,
              visibilitySettings: {
                ...acc.visibilitySettings,
                showToPartner: newSharingStatus,
                showTransactions: newSharingStatus,
                showBalance: newSharingStatus,
                showInNetWorth: newSharingStatus
              }
            };
          }
          return acc;
        }));

        toast.success(newSharingStatus ? 'Account shared!' : 'Account unshared');
      } else {
        throw new Error(result.error || 'Failed to update sharing');
      }
    } catch (error) {
      console.error('Error toggling account sharing:', error);
      toast.error('Failed to update: ' + error.message);
    } finally {
      setUpdatingAccounts(prev => {
        const newSet = new Set(prev);
        newSet.delete(accountId);
        return newSet;
      });
    }
  };

  if (!isOpen) return null;

  // Separate user and partner accounts
  const userAccounts = localAccounts.filter(acc => acc._ownedByUser);
  const partnerAccounts = localAccounts.filter(acc => !acc._ownedByUser);

  return (
    <Portal>
      <div className="fixed inset-0 z-50 overflow-auto bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Manage Account Sharing</h2>
              <p className="text-sm text-gray-600 mt-1">Control which accounts are shared with {partnerName || 'your partner'}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Your Accounts */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <span className="mr-2">Your Accounts</span>
                <span className="text-sm font-normal text-gray-500">
                  ({userAccounts.filter(acc => acc.sharedWithPartner || acc.visibilitySettings?.showToPartner).length} of {userAccounts.length} shared)
                </span>
              </h3>
              <div className="space-y-2">
                {userAccounts.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">No accounts found</p>
                ) : (
                  userAccounts.map((account) => {
                    const isShared = account.sharedWithPartner || account.visibilitySettings?.showToPartner || false;
                    const isUpdating = updatingAccounts.has(account._id);
                    const balance = account.currentBalance || account.balance || 0;

                    return (
                      <div
                        key={account._id}
                        className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-gray-900">
                              {account.name || account.bankName}
                            </p>
                            <span className="text-xs text-gray-500">
                              ••••{account.mask || account.lastFourDigits || ''}
                            </span>
                          </div>
                          <div className="flex items-center space-x-3 mt-1">
                            <p className="text-xs text-gray-600">
                              {account.institutionName || account.bankName || 'Manual Account'}
                            </p>
                            <span className="text-xs font-medium text-gray-700">
                              {formatCurrency(balance)}
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggle(account)}
                          disabled={isUpdating}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 ${
                            isShared ? 'bg-green-600' : 'bg-gray-300'
                          } ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                          role="switch"
                          aria-checked={isShared}
                          title={isShared ? `Shared with ${partnerName}` : 'Not shared'}
                        >
                          <span
                            aria-hidden="true"
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              isShared ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Partner's Accounts (Read-only) */}
            {partnerAccounts.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {partnerName}'s Accounts Shared with You
                </h3>
                <div className="space-y-2">
                  {partnerAccounts.map((account) => {
                    const balance = account.currentBalance || account.balance || 0;

                    return (
                      <div
                        key={account._id}
                        className="flex items-center justify-between p-4 bg-purple-50 border border-purple-200 rounded-lg"
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-gray-900">
                              {account.name || account.bankName}
                            </p>
                            <span className="text-xs text-gray-500">
                              ••••{account.mask || account.lastFourDigits || ''}
                            </span>
                          </div>
                          <div className="flex items-center space-x-3 mt-1">
                            <p className="text-xs text-gray-600">
                              {account.institutionName || account.bankName || 'Manual Account'}
                            </p>
                            <span className="text-xs font-medium text-gray-700">
                              {formatCurrency(balance)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-purple-700 font-medium">Read-only</span>
                          <div
                            className="relative inline-flex h-6 w-11 flex-shrink-0 rounded-full bg-green-600 border-2 border-transparent cursor-not-allowed opacity-60"
                            role="switch"
                            aria-checked="true"
                            title={`Shared by ${partnerName}`}
                          >
                            <span
                              aria-hidden="true"
                              className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 translate-x-5"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end">
            <button
              onClick={() => {
                onClose();
                // Trigger a refresh of wallet data
                if (onUpdate) onUpdate();
              }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default AccountSharingModal;

